import { Component, computed, inject, signal } from "@angular/core";
import { NgFor, NgIf, NgClass } from "@angular/common";
import { Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationEnd } from "@angular/router";
import { Store } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map, startWith } from "rxjs";
import { AuthActions } from "@store/auth/auth.actions";
import { selectAuthUser, selectIsAdmin, selectModuleAccess } from "@store/auth/auth.selectors";
import { ERP_MENU, MenuItem } from "./menu.config";
import { InstallAppComponent } from "@shared/components/install-app/install-app.component";
import { ChatWidgetComponent } from "@features/chat/chat-widget.component";
import { ErpAgentModalComponent } from "@features/erp-agent/erp-agent-modal.component";
import { ChangePasswordDialogComponent } from "@features/auth/change-password/change-password-dialog.component";
import { ModuleAccess } from "@core/models/user.model";
import { ErpModulesService } from "@core/services/erp-modules.service";
import { BRANDING } from "@core/branding/branding";
import { AuthService } from "@core/services/auth.service";
import { TenantService } from "@core/tenant/tenant.service";

@Component({
  selector: "erp-admin-layout",
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    InstallAppComponent,
    ChatWidgetComponent,
    ErpAgentModalComponent,
    ChangePasswordDialogComponent,
  ],
  template: `
    <div class="flex h-screen overflow-hidden bg-slate-100">
      <aside
        class="hidden h-screen w-72 shrink-0 border-r text-slate-200 lg:sticky lg:top-0 lg:block"
        [style.background-color]="b.sidebar.bgHex"
        [style.border-color]="b.sidebar.borderHex"
      >
        <div class="border-b p-4" [style.border-color]="b.sidebar.borderHex">
          <div class="text-lg font-black text-white">{{ b.companyName }}</div>
          <div class="mb-2 text-xs" [ngClass]="b.sidebar.subtitleClass">{{ b.systemName }}</div>
          <div class="relative">
            <input
              class="w-full rounded-xl border px-3 py-2 pr-8 text-sm text-slate-100 outline-none"
              [ngClass]="b.sidebar.searchInputClasses"
              placeholder="Buscar módulo..."
              [value]="menuSearch()"
              (input)="menuSearch.set($any($event.target).value)"
            />
            <button
              *ngIf="menuSearch()"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              (click)="menuSearch.set('')"
            >
              <svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <nav class="h-[calc(100vh-126px)] overflow-y-auto p-2">
          <ng-container *ngFor="let item of filteredMenu()">
            <!-- Módulo con hijos: botón colapsable -->
            <div *ngIf="item.children?.length" class="mb-0.5">
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-300 transition hover:bg-stone-900 hover:text-white"
                [ngClass]="{ 'bg-stone-900 text-white': isExpanded(item) }"
                (click)="toggleModule(item)"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0 text-slate-400">
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPaths[item.icon ?? '']" />
                </svg>
                <span class="flex-1 truncate">{{ item.label }}</span>
                <svg fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"
                  class="h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200"
                  [ngClass]="{ 'rotate-90': isExpanded(item) }">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              <!-- Hijos colapsables -->
              <div
                *ngIf="isExpanded(item)"
                class="ml-3 mt-0.5 space-y-0.5 border-l pl-3"
                [style.border-color]="b.sidebar.borderHex"
              >
                <ng-container *ngFor="let child of item.children">
                  <p *ngIf="child.groupLabel"
                     class="mt-2 mb-0.5 px-3 text-[9px] font-bold uppercase tracking-widest text-slate-600 select-none">
                    {{ child.groupLabel }}
                  </p>
                  <a
                    [routerLink]="child.route"
                    [routerLinkActive]="b.sidebar.activeLinkClasses"
                    class="block rounded-lg px-3 py-2 text-xs font-medium text-slate-400 transition hover:bg-stone-900 hover:text-white"
                  >
                    {{ child.label }}
                  </a>
                </ng-container>
              </div>
            </div>

            <!-- Módulo sin hijos: enlace directo -->
            <div *ngIf="!item.children?.length" class="mb-0.5">
              <a
                [routerLink]="item.route || '/dashboard'"
                [routerLinkActive]="b.sidebar.activeParentClasses"
                [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
                class="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-300 transition hover:bg-stone-900 hover:text-white"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0 text-slate-400">
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPaths[item.icon ?? '']" />
                </svg>
                <span class="truncate">{{ item.label }}</span>
              </a>
            </div>
          </ng-container>

          <div
            *ngIf="!filteredMenu().length"
            class="rounded-xl border p-4 text-sm text-slate-400"
            [style.border-color]="b.sidebar.borderHex"
          >
            No se encontraron módulos.
          </div>
        </nav>

        <!-- Acceso rápido a Ayuda / Plataforma fijo en el pie del sidebar -->
        <div class="border-t p-2 space-y-0.5" [style.border-color]="b.sidebar.borderHex">
          <a
            *ngIf="showSuperAdmin()"
            routerLink="/super-admin"
            [routerLinkActive]="b.sidebar.activeParentClasses"
            class="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-400 transition hover:bg-stone-900 hover:text-white"
          >
            <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M21.75 17.25v-.228a4.5 4.5 0 0 0-.12-1.03l-2.268-9.64a3.375 3.375 0 0 0-3.285-2.602H7.923a3.375 3.375 0 0 0-3.285 2.602l-2.268 9.64a4.5 4.5 0 0 0-.12 1.03v.228m19.5 0a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3m19.5 0a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3m16.5 0h.008v.008h-.008v-.008Zm-3 0h.008v.008h-.008v-.008Z" />
            </svg>
            <span>Plataforma / Tenants</span>
          </a>
          <a
            routerLink="/ayuda"
            [routerLinkActive]="b.sidebar.activeParentClasses"
            class="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-400 transition hover:bg-stone-900 hover:text-white"
          >
            <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
            </svg>
            <span>Centro de Ayuda</span>
          </a>
        </div>
      </aside>

      <div class="min-w-0 flex-1 flex flex-col overflow-hidden">
        <header
          class="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:px-6"
        >
          <div class="flex items-center justify-between gap-4">
            <button
              class="rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 lg:hidden"
              (click)="mobileOpen.set(!mobileOpen())"
            >
              Menú
            </button>

            <div class="min-w-0">
              <div class="text-sm font-bold text-slate-900">{{ b.companyName }}</div>
              <div class="truncate text-xs text-slate-500">{{ b.systemTagline }}</div>
            </div>

            <div class="flex items-center gap-2">
              <!--
                Acceso a la plataforma multi-tenant. Aparece bajo las mismas dos
                condiciones que exige superAdminGuard —rol SUPER_ADMIN y host
                maestro—, para que nunca se muestre un botón que al pulsarlo
                rebote al dashboard sin explicación.
              -->
              <a
                *ngIf="showSuperAdmin()"
                routerLink="/super-admin"
                routerLinkActive="ring-2 ring-violet-300"
                title="Plataforma: alta y administración de tenants"
                class="flex items-center gap-2 rounded-xl border border-violet-300 bg-violet-600 px-3 py-2 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-violet-700 hover:shadow-md hover:-translate-y-px active:translate-y-0"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4 shrink-0">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                </svg>
                <span class="hidden sm:inline">Plataforma</span>
              </a>

              <a
                routerLink="/ayuda"
                [routerLinkActive]="b.header.ayudaBtnActiveClasses"
                title="Centro de Ayuda"
                class="group relative flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-px active:translate-y-0"
                [ngClass]="b.header.ayudaBtnClasses"
              >
                <!-- Pulso animado -->
                <span class="relative flex h-2 w-2 shrink-0">
                  <span class="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 group-hover:opacity-0" [ngClass]="b.header.ayudaPingClass"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full group-hover:bg-white" [ngClass]="b.header.ayudaDotClass"></span>
                </span>
                <svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4 shrink-0">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                </svg>
                <span class="hidden sm:inline">Ayuda</span>
              </a>
              <button
                type="button"
                (click)="passwordOpen.set(true)"
                title="Cambiar mi contraseña"
                class="group flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100"
              >
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                  [ngClass]="b.header.userAvatarClasses"
                >
                  {{ userInitials() }}
                </div>
                <span class="hidden text-sm font-medium text-slate-700 sm:block">{{ userName() }}</span>
                <svg fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"
                     class="hidden h-4 w-4 shrink-0 text-slate-400 group-hover:text-slate-700 sm:block">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                </svg>
              </button>
              <button
                class="group flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition-all duration-200 hover:border-red-400 hover:bg-red-500 hover:text-white hover:shadow-md hover:shadow-red-200 hover:-translate-y-px active:translate-y-0"
                (click)="logout()"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
                  class="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
                </svg>
                <span class="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </header>

        <erp-change-password-dialog
          *ngIf="passwordOpen()"
          (closed)="passwordOpen.set(false)"
        />

        <!-- Menú móvil -->
        <div
          *ngIf="mobileOpen()"
          class="max-h-[78vh] overflow-y-auto border-b p-3 text-slate-200 lg:hidden"
          [style.background-color]="b.sidebar.bgHex"
          [style.border-color]="b.sidebar.borderHex"
        >
          <input
            class="mb-3 w-full rounded-xl border px-3 py-2 text-sm text-slate-100 outline-none"
            [ngClass]="b.sidebar.searchInputClasses"
            placeholder="Buscar módulo..."
            [value]="menuSearch()"
            (input)="menuSearch.set($any($event.target).value)"
          />

          <ng-container *ngFor="let item of filteredMenu()">
            <!-- Con hijos -->
            <div *ngIf="item.children?.length" class="mb-0.5">
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-200 transition hover:bg-stone-900"
                [ngClass]="{ 'bg-stone-900': isExpanded(item) }"
                (click)="toggleModule(item)"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0 text-slate-400">
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPaths[item.icon ?? '']" />
                </svg>
                <span class="flex-1">{{ item.label }}</span>
                <svg fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"
                  class="h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200"
                  [ngClass]="{ 'rotate-90': isExpanded(item) }">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              <div *ngIf="isExpanded(item)" class="ml-4 mt-0.5 space-y-0.5 border-l pl-3" [style.border-color]="b.sidebar.borderHex">
                <ng-container *ngFor="let child of item.children">
                  <p *ngIf="child.groupLabel"
                     class="mt-2 mb-0.5 px-3 text-[9px] font-bold uppercase tracking-widest text-slate-600 select-none">
                    {{ child.groupLabel }}
                  </p>
                  <a
                    [routerLink]="child.route"
                    [routerLinkActive]="b.sidebar.activeLinkClasses"
                    class="block rounded-lg px-3 py-2 text-xs font-medium text-slate-400 hover:bg-stone-900 hover:text-white"
                    (click)="mobileOpen.set(false)"
                  >
                    {{ child.label }}
                  </a>
                </ng-container>
              </div>
            </div>

            <!-- Sin hijos -->
            <div *ngIf="!item.children?.length" class="mb-0.5">
              <a
                [routerLink]="item.route || '/dashboard'"
                [routerLinkActive]="b.sidebar.activeParentClasses"
                class="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-black text-slate-200 hover:bg-stone-900"
                (click)="mobileOpen.set(false)"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0 text-slate-400">
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPaths[item.icon ?? '']" />
                </svg>
                {{ item.label }}
              </a>
            </div>
          </ng-container>

          <!-- Plataforma / Tenants (solo SUPER_ADMIN en el tenant maestro) -->
          <a
            *ngIf="showSuperAdmin()"
            routerLink="/super-admin"
            [routerLinkActive]="b.sidebar.activeParentClasses"
            class="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-black text-slate-200 hover:bg-stone-900"
            (click)="mobileOpen.set(false)"
          >
            <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 shrink-0 text-slate-400">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M21.75 17.25v-.228a4.5 4.5 0 0 0-.12-1.03l-2.268-9.64a3.375 3.375 0 0 0-3.285-2.602H7.923a3.375 3.375 0 0 0-3.285 2.602l-2.268 9.64a4.5 4.5 0 0 0-.12 1.03v.228m19.5 0a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3m19.5 0a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3m16.5 0h.008v.008h-.008v-.008Zm-3 0h.008v.008h-.008v-.008Z" />
            </svg>
            Plataforma / Tenants
          </a>
        </div>

        <main class="flex-1 min-h-0 overflow-y-auto p-4 lg:p-6">
          <router-outlet />
        </main>
      </div>
      <erp-install-app />
      <erp-chat-widget />
      <erp-agent-modal />
    </div>
  `,
})
export class AdminLayoutComponent {
  protected readonly b = BRANDING;
  private readonly store     = inject(Store);
  private readonly router    = inject(Router);
  private readonly erpModules = inject(ErpModulesService);
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);

  /** Visible únicamente cuando el super-admin.guard dejaría pasar: rol SUPER_ADMIN (5) + tenant maestro. */
  readonly showSuperAdmin = computed(() => this.authService.getRoleId() === 5 && this.tenantService.esHostMaestro());

  private readonly authUser    = toSignal(this.store.select(selectAuthUser));
  private readonly isAdmin     = toSignal(this.store.select(selectIsAdmin));
  private readonly moduleAccess = toSignal(this.store.select(selectModuleAccess));

  readonly userName = computed(() => {
    const u = this.authUser() as any;
    if (u?.name)  return String(u.name);
    if (u?.email) return String(u.email).split('@')[0];
    return '';
  });

  readonly userInitials = computed(() => {
    const u = this.authUser() as any;
    const raw: string = u?.name ?? u?.email ?? '';
    if (!raw) return '?';
    const word = raw.includes('@') ? raw.split('@')[0] : raw;
    return word.split(/[\s._-]+/).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase() || '?';
  });

  mobileOpen    = signal(false);
  menuSearch    = signal("");
  expandedItems = signal<Set<string>>(new Set());
  closedItems   = signal<Set<string>>(new Set());

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    )
  );

  isExpanded(item: MenuItem): boolean {
    if (this.menuSearch()) return true;
    // Cierre manual tiene prioridad sobre auto-expand por ruta
    if (this.closedItems().has(item.label)) return false;
    if (this.expandedItems().has(item.label)) return true;
    const url = this.currentUrl() ?? '';
    return item.children?.some(c => c.route && url.startsWith(c.route)) ?? false;
  }

  private moduleRoute(item: MenuItem): string | null {
    if (item.route) return item.route;
    const first = item.children?.[0]?.route;
    if (!first) return null;
    const seg = first.split('/').filter(Boolean)[0];
    return seg ? '/' + seg : null;
  }

  toggleModule(item: MenuItem): void {
    if (this.isExpanded(item)) {
      this.closedItems.update(s => new Set([...s, item.label]));
      this.expandedItems.update(s => { const n = new Set(s); n.delete(item.label); return n; });
    } else {
      this.closedItems.update(s => { const n = new Set(s); n.delete(item.label); return n; });
      this.expandedItems.update(s => new Set([...s, item.label]));
      const route = this.moduleRoute(item);
      if (route) this.router.navigate([route]);
    }
  }

  readonly iconPaths: Record<string, string> = {
    dashboard:     'M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z',
    admin:         'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z',
    security:      'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
    crm:           'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z',
    finance:       'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z',
    accounting:    'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25',
    purchases:     'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z',
    inventory:     'm21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9',
    construction:  'M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 0 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z',
    sales:         'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006-3.75 3.75m0 0-3.75-3.75m3.75 3.75V8.706M9 4.5h6',
    realestate:    'M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205 3 1m1.5.5-1.5-.5M6.75 7.364V3h-3v18m3-13.636 10.5-3.819',
    payroll:       'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z',
    access:        'M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z',
    postventa:     'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99',
    documents:     'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
    portalclients: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
    payments:      'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z',
    supply:        'M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
    projects:      'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
    precios:       'M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V13.5Zm0 2.25h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V18Zm2.498-6.75h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V13.5Zm0 2.25h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V18Zm2.504-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5Zm0 2.25h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V18Zm2.498-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5ZM8.25 6h7.5v2.25h-7.5V6ZM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.756 4.5 4.981V19.5a2.25 2.25 0 0 0 2.25 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25V4.981c0-1.225-.807-2.282-1.907-2.409A41.146 41.146 0 0 0 12 2.25Z',
    planning:      'M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605',
    assets:        'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125',
    hr:            'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z',
    manufacturing: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
    maintenance:   'M21.75 6.75a4.5 4.5 0 0 1-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 1 1-3.586-3.585l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 0 1 6.336-4.486l-3.276 3.276a3.004 3.004 0 0 0 2.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852Z',
    quality:       'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z',
    pos:           'M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185ZM9.75 9h.008v.008H9.75V9Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008V13.5Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z',
    ecommerce:     'M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z',
    bi:            'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
    bsc:           'M3 3l1.664 1.664M21 21l-1.5-1.5m-5.485-1.242L13.5 12l1.5.75M6.75 6.75l.75 1.5-1.5.75M12 3.75l.75 1.5L12 6l-.75-1.5L12 3.75ZM9.75 9.75l.75.75m-.75-.75-.75.75m4.5-4.5.75.75m-.75-.75-.75.75M3.75 12c0 4.556 3.694 8.25 8.25 8.25S20.25 16.556 20.25 12 16.556 3.75 12 3.75 3.75 7.444 3.75 12Z',
    bim:           'M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244',
    workflows:     'M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75',
    integrations:  'M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z',
    portals:       'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253M3.284 14.253A8.959 8.959 0 0 1 3 12c0-1.536.38-2.984 1.051-4.254',
    bitacora:      'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z',
    salud:         'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z',
    reports:       'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25M9 16.5v.75m3-3v3M15 12v5.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
    local_parking: 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z',
    whatsapp:      'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155',
    condominios:   'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
    cart:          'M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016 2.993 2.993 0 0 0 2.25-1.015 3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z',
    contracts:     'M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75',
    shield:        'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
    imss:          'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z',
    helpdesk:      'M16.712 4.33a9.027 9.027 0 0 1 1.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 0 0-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 0 1 0 9.424m-4.138-5.976a3.736 3.736 0 0 0-.88-1.388 3.737 3.737 0 0 0-1.388-.88m2.268 2.268a3.765 3.765 0 0 1 0 2.528m-2.268-4.796a3.765 3.765 0 0 0-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 0 1-1.388.88m2.268-2.268 4.138 3.448m0 0a9.027 9.027 0 0 1-1.306 1.652c-.51.51-1.064.944-1.652 1.306m0 0-3.448-4.138m3.448 4.138a9.014 9.014 0 0 1-9.424 0m5.976-4.138a3.765 3.765 0 0 1-2.528 0m0 0a3.736 3.736 0 0 1-1.388-.88 3.737 3.737 0 0 1-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 0 1-1.652-1.306 9.027 9.027 0 0 1-1.306-1.652m0 0 4.138-3.448M4.33 16.712a9.014 9.014 0 0 1 0-9.424m4.138 5.976a3.765 3.765 0 0 1 0-2.528m0 0c.181-.506.475-.982.88-1.388a3.736 3.736 0 0 1 1.388-.88m-2.268 2.268L4.33 7.288m6.406 1.18L7.288 4.33m0 0a9.024 9.024 0 0 0-1.652 1.306A9.025 9.025 0 0 0 4.33 7.288',
    repse:         'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z',
    drive:         'M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z',
    sst:           'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z',
    mailing:       'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75',
    robot:         'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z',
  };

  private readonly ADMIN_ALWAYS_VISIBLE = new Set(['SEGURIDAD', 'ADMINISTRACION']);

  private readonly accessibleMenu = computed(() => {
    const byRole  = this.filterMenuByAccess(ERP_MENU, this.moduleAccess() ?? [], this.isAdmin() ?? false);
    const modules = this.erpModules.modules();
    const isAdmin = this.isAdmin() ?? false;
    if (!modules.length) return byRole; // aún no cargó: mostrar todo
    return byRole
      .map(item => {
        // Seguridad y Administración siempre visibles para el admin
        if (isAdmin && item.moduleCode && this.ADMIN_ALWAYS_VISIBLE.has(item.moduleCode)) return item;

        // Módulo padre activo
        if (item.moduleCode && !this.erpModules.isActive(item.moduleCode)) return null;

        if (!item.children?.length) return item;

        // Filtrar hijos: solo ocultar si están explícitamente desactivados en la lista
        // (si no están registrados en erp_modules se muestran)
        const visibleChildren = item.children.filter(child => {
          if (!child.moduleCode) return true;
          const mod = modules.find(m => m.moduleCode === child.moduleCode);
          return !mod || mod.isActive || mod.isRequired;
        });
        return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
      })
      .filter((item): item is MenuItem => item !== null);
  });

  filteredMenu = computed(() => this.filterMenu(this.accessibleMenu(), this.menuSearch()));

  /** Diálogo de cambio de contraseña. */
  readonly passwordOpen = signal(false);

  logout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  private filterMenuByAccess(menu: MenuItem[], access: ModuleAccess[], isAdmin: boolean): MenuItem[] {
    if (isAdmin) return menu;

    return menu
      .map(item => {
        // Ítems sin moduleCode (ej. Dashboard) siempre visibles
        if (!item.moduleCode) return item;

        // Verificar acceso al módulo padre
        const parentAccess = access.find(a => a.moduleCode === item.moduleCode);
        if (!parentAccess?.canView) return null;

        // Sin hijos → devolver tal cual
        if (!item.children?.length) return item;

        // Verificar si existen permisos de submódulo para este padre
        const hasChildPerms = item.children.some(child => {
          if (!child.route) return false;
          const code = this.deriveChildCode(item.moduleCode!, child.route);
          return access.some(a => a.moduleCode === code);
        });

        // Sin permisos de submódulo configurados → mostrar todos los hijos
        if (!hasChildPerms) return item;

        // Filtrar hijos por sus permisos individuales
        const visibleChildren = item.children.filter(child => {
          if (!child.route) return true;
          const code = this.deriveChildCode(item.moduleCode!, child.route);
          const ca = access.find(a => a.moduleCode === code);
          return ca ? ca.canView : true;
        });

        return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
      })
      .filter((item): item is MenuItem => item !== null);
  }

  private deriveChildCode(parentCode: string, route: string): string {
    const slug = (route.split('/').pop() ?? '').toUpperCase().replace(/-/g, '_');
    return `${parentCode}.${slug}`;
  }

  private filterMenu(menu: MenuItem[], term: string): MenuItem[] {
    const query = term.trim().toLowerCase();
    if (!query) return menu;

    return menu
      .map((item) => {
        const itemMatches = item.label.toLowerCase().includes(query);
        const children = item.children?.filter(
          (child) =>
            child.label.toLowerCase().includes(query) ||
            child.route?.toLowerCase().includes(query),
        );

        if (itemMatches) return item;
        if (children?.length) return { ...item, children };
        return undefined;
      })
      .filter((item): item is MenuItem => Boolean(item));
  }
}
